import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Form, Button, Card, Spinner, Alert, Row, Col } from 'react-bootstrap'
import { usePriceExtractionsFetch } from '../../../hooks/fetch/usePriceExtractionsFetch'
import { ExtractionType } from '../../../interfaces/priceExtraction'
import type { ProjectOption } from '../../../interfaces/priceExtraction'
import SpecialSelect, { OptionSelectType } from '../../../components/ui/SpecialSelect'

// Interface para archivos con preview
interface FileWithPreview {
    file: File
    preview: string | null
    id: string
}

// Constantes de limites
const MAX_FILES = 5
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_TOTAL_SIZE = 30 * 1024 * 1024 // 30MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']

// Países disponibles (hardcoded para evitar llamada extra)
const COUNTRIES = [
    { id: 1, name: 'Uruguay' },
    { id: 2, name: 'Paraguay' },
    { id: 3, name: 'Bolivia' },
    { id: 4, name: 'España' },
    { id: 5, name: 'Colombia' },
    { id: 6, name: 'Panamá' },
    { id: 7, name: 'Chile' },
    { id: 8, name: 'República Dominicana' },
    { id: 9, name: 'Costa Rica' }
]

const DEFAULT_COUNTRY: OptionSelectType = { value: 1, label: 'Uruguay' }

export const ExtractionForm = () => {
    const navigate = useNavigate()
    const { createExtraction, getProjectsByCountry, showAlertMessage } = usePriceExtractionsFetch()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [projects, setProjects] = useState<ProjectOption[]>([])
    const [loadingProjects, setLoadingProjects] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [initialized, setInitialized] = useState(false)

    // Form state - Uruguay seleccionado por defecto
    const [selectedCountry, setSelectedCountry] = useState<OptionSelectType>(DEFAULT_COUNTRY)
    const [selectedProjects, setSelectedProjects] = useState<OptionSelectType[]>([])
    const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([])
    const [extractionType, setExtractionType] = useState<ExtractionType>('lite')
    const [description, setDescription] = useState('')
    const [isPasteZoneFocused, setIsPasteZoneFocused] = useState(false)

    // Validation state
    const [errors, setErrors] = useState<{ country?: boolean; projects?: boolean; file?: boolean }>({})

    // Cargar proyectos de Uruguay al iniciar
    const loadInitialProjects = async () => {
        setLoadingProjects(true)
        const result = await getProjectsByCountry(DEFAULT_COUNTRY.value as number)
        setLoadingProjects(false)
        if (result.success) {
            setProjects(result.data)
        }
    }

    // Inicializar datos una sola vez
    if (!initialized) {
        setInitialized(true)
        loadInitialProjects()
    }

    // Calcular tamano total de archivos seleccionados
    const getTotalSize = useCallback(() => {
        return selectedFiles.reduce((sum, f) => sum + f.file.size, 0)
    }, [selectedFiles])

    // Procesar archivo seleccionado (file input o paste)
    const processFile = useCallback((file: File) => {
        // Validar tipo
        if (!ALLOWED_TYPES.includes(file.type)) {
            showAlertMessage('danger', 'Tipo de archivo no permitido. Use PDF, JPG, PNG o WebP.')
            return false
        }

        // Validar tamano individual
        if (file.size > MAX_FILE_SIZE) {
            showAlertMessage('danger', `El archivo ${file.name} es muy grande. Maximo 10MB.`)
            return false
        }

        // Validar limite de archivos
        if (selectedFiles.length >= MAX_FILES) {
            showAlertMessage('danger', `Maximo ${MAX_FILES} archivos permitidos.`)
            return false
        }

        // Validar tamano total
        const currentTotal = selectedFiles.reduce((sum, f) => sum + f.file.size, 0)
        if (currentTotal + file.size > MAX_TOTAL_SIZE) {
            showAlertMessage('danger', 'El tamano total excede 30MB.')
            return false
        }

        setErrors(prev => ({ ...prev, file: false }))

        // Crear ID unico para el archivo
        const id = crypto.randomUUID()

        // Crear preview si es imagen
        if (file.type.startsWith('image/')) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setSelectedFiles(prev => [...prev, { file, preview: reader.result as string, id }])
            }
            reader.readAsDataURL(file)
        } else {
            setSelectedFiles(prev => [...prev, { file, preview: null, id }])
        }
        return true
    }, [showAlertMessage, selectedFiles])

    // Manejar paste de imagenes desde el portapapeles (soporta multiples)
    const handlePaste = useCallback((e: ClipboardEvent) => {
        const items = e.clipboardData?.items
        if (!items) return

        let imagesAdded = 0
        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.type.startsWith('image/')) {
                e.preventDefault()
                const file = item.getAsFile()
                if (file) {
                    // Generar nombre con timestamp
                    const extension = item.type.split('/')[1] || 'png'
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
                    const renamedFile = new File([file], `clipboard-${timestamp}-${i}.${extension}`, { type: file.type })
                    if (processFile(renamedFile)) {
                        imagesAdded++
                    }
                }
            }
        }
        if (imagesAdded > 0) {
            showAlertMessage('success', `${imagesAdded} imagen(es) pegada(s) desde el portapapeles`)
        }
    }, [processFile, showAlertMessage])

    // Escuchar eventos de paste en toda la ventana
    useEffect(() => {
        const handler = handlePaste as EventListener
        document.addEventListener('paste', handler)
        return () => {
            document.removeEventListener('paste', handler)
        }
    }, [handlePaste])

    const handleCountryChange = async (option: OptionSelectType) => {
        setSelectedCountry(option)
        setSelectedProjects([])
        setProjects([])
        setErrors(prev => ({ ...prev, country: false }))

        if (option.value) {
            setLoadingProjects(true)
            const result = await getProjectsByCountry(option.value)
            setLoadingProjects(false)
            if (result.success) {
                setProjects(result.data)
            }
        }
    }

    const handleProjectsChange = (options: OptionSelectType[]) => {
        setSelectedProjects(options)
        setErrors(prev => ({ ...prev, projects: false }))
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        files.forEach(file => processFile(file))
        // Reset input para permitir re-seleccion del mismo archivo
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const removeFile = (id: string) => {
        setSelectedFiles(prev => prev.filter(f => f.id !== id))
    }

    const clearAllFiles = () => {
        setSelectedFiles([])
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {}

        if (!selectedCountry?.value) {
            newErrors.country = true
        }
        if (selectedProjects.length === 0) {
            newErrors.projects = true
        }
        if (selectedFiles.length === 0) {
            newErrors.file = true
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            showAlertMessage('danger', 'Complete todos los campos requeridos')
            return
        }

        setSubmitting(true)

        const projectIds = selectedProjects.map(p => p.value as number)

        const result = await createExtraction(
            selectedCountry!.value as number,
            projectIds,
            selectedFiles.map(f => f.file),
            extractionType,
            description || undefined
        )

        setSubmitting(false)

        if (result.success && result.data) {
            showAlertMessage('success', `Extraccion completada. ${result.data.changesPending} cambios detectados.`)
            navigate(`/admin/price-extractions/validate/${result.data.id}`)
        } else {
            showAlertMessage('danger', result.message || 'Error al procesar la extraccion')
        }
    }

    const countryOptions: OptionSelectType[] = COUNTRIES.map(c => ({
        value: c.id,
        label: c.name
    }))

    const projectOptions: OptionSelectType[] = projects.map(p => ({
        value: p.id,
        label: `${p.id} - ${p.title}`
    }))

    return (
        <div className="p-5">
            <div className="mb-4 py-2 px-4 d-flex justify-content-between flex-wrap position-relative">
                <Link to="/admin/price-extractions" className="fs-2 go-back mt-3 ms-3 ms-md-4">
                    <i className="text-light bi bi-arrow-left"></i>
                </Link>
                <h3>NUEVA EXTRACCION DE PRECIOS</h3>
                <div style={{ width: 150 }}></div>
            </div>

            <Card className="mx-auto" style={{ maxWidth: 700 }}>
                <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        {/* Pais */}
                        <Form.Group className="mb-3">
                            <Form.Label>Pais *</Form.Label>
                            <SpecialSelect
                                options={countryOptions}
                                onChange={handleCountryChange}
                                selected={selectedCountry}
                                placeholder="Seleccione un pais"
                                noOptionsMessage="No hay paises disponibles"
                                setInvalid={errors.country}
                            />
                        </Form.Group>

                        {/* Proyectos */}
                        <Form.Group className="mb-3">
                            <Form.Label>Proyectos *</Form.Label>
                            <SpecialSelect
                                options={projectOptions}
                                onChange={handleProjectsChange}
                                selected={selectedProjects}
                                loading={loadingProjects}
                                loadingMessage="Cargando proyectos..."
                                placeholder="Buscar proyectos..."
                                noOptionsMessage="No hay proyectos disponibles"
                                isMulti={true}
                                setInvalid={errors.projects}
                            />
                            {selectedProjects.length > 0 && (
                                <Form.Text className="text-muted">
                                    {selectedProjects.length} proyecto(s) seleccionado(s)
                                </Form.Text>
                            )}
                        </Form.Group>

                        {/* Archivos */}
                        <Form.Group className="mb-3">
                            <Form.Label>Archivos de lista de precios *</Form.Label>

                            {/* Zona de archivos */}
                            <div
                                className={`border rounded p-3 mb-2 ${errors.file ? 'border-danger' : isPasteZoneFocused ? 'border-primary' : 'border-secondary'}`}
                                style={{
                                    backgroundColor: isPasteZoneFocused ? '#f0f7ff' : selectedFiles.length > 0 ? '#f8fff8' : '#fafafa',
                                    transition: 'all 0.2s ease'
                                }}
                                tabIndex={0}
                                onFocus={() => setIsPasteZoneFocused(true)}
                                onBlur={() => setIsPasteZoneFocused(false)}
                            >
                                {selectedFiles.length > 0 ? (
                                    <>
                                        {/* Grid de archivos */}
                                        <Row className="g-2 mb-2">
                                            {selectedFiles.map(f => (
                                                <Col xs={6} md={4} key={f.id}>
                                                    <div className="border rounded p-2 position-relative bg-white">
                                                        {f.preview ? (
                                                            <img
                                                                src={f.preview}
                                                                alt=""
                                                                style={{ maxHeight: 80, width: '100%', objectFit: 'contain' }}
                                                                className="rounded"
                                                            />
                                                        ) : (
                                                            <div className="text-center py-2">
                                                                <i className="bi bi-file-earmark-pdf text-danger" style={{ fontSize: '2rem' }}></i>
                                                            </div>
                                                        )}
                                                        <small className="d-block text-truncate mt-1" title={f.file.name}>
                                                            {f.file.name}
                                                        </small>
                                                        <small className="text-muted">
                                                            {(f.file.size / 1024 / 1024).toFixed(1)}MB
                                                        </small>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1 p-0 px-1"
                                                            onClick={() => removeFile(f.id)}
                                                            title="Eliminar archivo"
                                                        >
                                                            <i className="bi bi-x"></i>
                                                        </button>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Footer con estadisticas y botones */}
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <small className="text-muted">
                                                {selectedFiles.length}/{MAX_FILES} archivos - {(getTotalSize() / 1024 / 1024).toFixed(1)}MB total
                                            </small>
                                            <div className="d-flex gap-2">
                                                {selectedFiles.length < MAX_FILES && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => fileInputRef.current?.click()}
                                                    >
                                                        <i className="bi bi-plus me-1"></i>
                                                        Agregar
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={clearAllFiles}
                                                >
                                                    <i className="bi bi-trash me-1"></i>
                                                    Limpiar
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        className="text-center"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <i className="bi bi-cloud-upload text-secondary" style={{ fontSize: '2.5rem' }}></i>
                                        <p className="mb-1 mt-2">
                                            <strong>Haz click para seleccionar</strong> o <strong>Ctrl+V</strong> para pegar imagenes
                                        </p>
                                        <small className="text-muted">
                                            PDF, JPG, PNG, WebP (Max 10MB c/u, hasta {MAX_FILES} archivos)
                                        </small>
                                    </div>
                                )}
                            </div>

                            <Form.Control
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                multiple
                                style={{ display: 'none' }}
                            />

                            {isPasteZoneFocused && selectedFiles.length === 0 && (
                                <Form.Text className="text-primary">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Puedes pegar imagenes directamente con Ctrl+V (o Cmd+V en Mac)
                                </Form.Text>
                            )}
                        </Form.Group>

                        {/* Tipo de extraccion */}
                        <Form.Group className="mb-3">
                            <Form.Label>Tipo de extraccion</Form.Label>
                            <div>
                                <Form.Check
                                    inline
                                    type="radio"
                                    id="type-lite"
                                    label="Lite (1 analisis)"
                                    name="extractionType"
                                    checked={extractionType === 'lite'}
                                    onChange={() => setExtractionType('lite')}
                                />
                                <Form.Check
                                    inline
                                    type="radio"
                                    id="type-pro"
                                    label="Pro (3 analisis en paralelo)"
                                    name="extractionType"
                                    checked={extractionType === 'pro'}
                                    onChange={() => setExtractionType('pro')}
                                />
                            </div>
                            <Form.Text className="text-muted">
                                {extractionType === 'lite'
                                    ? 'Analisis rapido con una sola ejecucion de IA.'
                                    : 'Analisis mas preciso con 3 ejecuciones paralelas y deteccion de discrepancias.'}
                            </Form.Text>
                        </Form.Group>

                        {/* Descripcion */}
                        <Form.Group className="mb-4">
                            <Form.Label>Descripcion / Instrucciones (opcional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Instrucciones adicionales para la IA sobre como interpretar el archivo..."
                            />
                            <Form.Text className="text-muted">
                                Ej: "Los precios estan en UF", "Ignorar la columna de descuentos"
                            </Form.Text>
                        </Form.Group>

                        {/* Botones */}
                        <div className="d-flex justify-content-end gap-2">
                            <Link to="/admin/price-extractions">
                                <Button variant="outline-secondary" disabled={submitting}>
                                    Cancelar
                                </Button>
                            </Link>
                            <Button type="submit" variant="primary" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Procesando...
                                    </>
                                ) : (
                                    'Procesar Extraccion'
                                )}
                            </Button>
                        </div>

                        {submitting && (
                            <Alert variant="info" className="mt-3">
                                <strong>Procesando extraccion...</strong>
                                <p className="mb-0 mt-2">
                                    Esto puede tomar entre 30 segundos y 2 minutos dependiendo del tamano del archivo
                                    y la cantidad de proyectos seleccionados.
                                </p>
                            </Alert>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </div>
    )
}
